import styled from "styled-components";
import {
  TFullData,
  TSelection,
  TTableHead,
  TTableHeadIconStyles,
  TTableHeadIcons,
  TTableHeadWithIcon,
} from ".";
import { UilInfoCircle } from "@iconscout/react-unicons";
import Tooltip, { TTooltipProps } from "../Tooltip";
import Skeleton from "../Skeleton";

const Container = styled.thead<{
  $customTopPosition?: string;
  $customPaddingTop?: string;
}>`
  height: 44px;
  padding-top: ${({ $customPaddingTop }) => $customPaddingTop || "24px"};
  display: block;
  background: white;
  top: ${({ $customTopPosition }) => $customTopPosition ?? "0px"};
  position: sticky;
  z-index: 1;
`;

const RowContainer = styled.tr<{ $gridTemplateColumns: string }>`
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  align-items: center;
  box-sizing: border-box;
  position: relative;
  height: 44px;
  border-radius: 8px;
  border: 1px solid var(--color-light-200);
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);
`;

const HeadContainer = styled.th<{ $cell?: TTableHeadWithIcon }>`
  display: block;
  font-weight: normal;
  font-size: 14px;
  font-family: var(--font-paragraph-default);
  color: var(--color-text-title);

  &.border {
    display: none;
    box-sizing: border-box;
    position: absolute;
    width: 100%;
    left: 0;
    height: 44px;
    border-radius: 8px;
    border: 1px solid var(--color-light-200);
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  }

  .headerTooltip {
    position: relative;
    display: flex;
    align-items: center;

    & > p {
      display: inline;
    }
  }

  svg {
    width: 16px;
    height: 16px;
    ${({ $cell }) =>
      $cell?.style === "icon"
        ? `color: var(${$cell.iconColor});` +
          `${
            $cell.iconPosition === "left"
              ? "padding-right: 5px"
              : "padding-left: 5px"
          }`
        : ""};
    vertical-align: middle;
  }

`;

type TTHeadProps = {
  tableHead: {
    customTopPosition?: string;
    customPaddingTop?: string;
    value: TTableHead<TTableHeadIconStyles>[];
  };
  selectType: TFullData<TSelection>;
  onSelect?: (
    clickStatus: "all" | "none",
    setSelected: React.Dispatch<
      React.SetStateAction<"" | "active" | "inactive">
    >
  ) => void;
  gridTemplateColumns: string;
};

type THeadControlledProps = {
  checked?: boolean;
  indeterminate?: boolean;
};

const getSkeletonWidth = (value: string) => {
  return `${Math.max(60, value.length * 8 + 24)}px`;
};

const THead: React.FC<TTHeadProps & THeadControlledProps> = ({
  tableHead,
  selectType,
  gridTemplateColumns,
}) => {
  const handleIconPositionLeft = (a: JSX.Element, b: string) => (
    <div>
      {a}
      <p style={{ display: "inline" }}>{b}</p>
    </div>
  );
  const handleIconPositionRight = (
    a: string,
    b: JSX.Element,
    hasTooltip?: boolean,
    tooltipProps?: Omit<TTooltipProps, "children">
  ) => (
    <div className="headerTooltip">
      <p style={{ display: "inline" }}>{a}</p>
      {hasTooltip && tooltipProps ? (
        <Tooltip {...tooltipProps}>{b}</Tooltip>
      ) : (
        b
      )}
    </div>
  );

  const switchCaseHandler = (icon: TTableHeadIcons) => {
    switch (icon) {
      case "infoCircle":
        return <UilInfoCircle />;
    }
  };

  const renderContentByPickedIcon = (
    cell: TTableHead<TTableHeadIconStyles>
  ) => {
    if (cell.style === "icon") {
      return cell.iconPosition === "left"
        ? handleIconPositionLeft(switchCaseHandler(cell.icon), cell.value)
        : handleIconPositionRight(
            cell.value,
            switchCaseHandler(cell.icon),
            cell.icon === "infoCircle",
            cell.tooltip
          );
    }
    return cell.value;
  };

  return (
    <Container
      $customTopPosition={tableHead.customTopPosition}
      $customPaddingTop={tableHead.customPaddingTop}
    >
      <RowContainer $gridTemplateColumns={gridTemplateColumns}>
        <th>
          <div></div>
        </th>
        <HeadContainer>
          {selectType.type === "multiSelect" ? (
            <div style={{ width: 18 }}></div>
          ) : (
            <div style={{ width: 18 }}></div>
          )}
        </HeadContainer>

        {tableHead.value.map((cell, index) => (
          <HeadContainer
            key={index}
            $cell={cell.style === "icon" ? cell : undefined}
          >
            {cell.showSkeleton ? (
              <Skeleton
                variant="box"
                width={getSkeletonWidth(
                  typeof cell.value === "string" ? cell.value : ""
                )}
                height={30}
              />
            ) : (
              renderContentByPickedIcon(cell)
            )}
          </HeadContainer>
        ))}
      </RowContainer>
    </Container>
  );
};

export default THead;
